import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../hooks/useAppSelector'
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { fetchSubDepartments, fetchProducts, resetCompletedSubDepartments, resetProducts, setCompletedSubDepartments, setCurrentProduct, setSelectedSubDepartment } from '../../store/features/product/continous-product-slice';
import { ApplicationSubDepartment } from '../../models/organisation/application-sub-department';
import styled from 'styled-components';
import { fetchByProductId } from '../../store/features/ordering/basket/basket-slice';
import { ApplicationBasketItem } from '../../models/order/basket/application-basket-item';
import LinearProgress from '@mui/material/LinearProgress';
import LoadingSpinner from '../../components/controls/loading-spinner';
import { GoogleIcon, ICON_ID } from '../../components/icons/google-icon';
import { showError } from '../../components/notification/toastr-actions';
import ProductDrawer from './product-drawer';
import Button from '../../components/controls/button';
import { DatabaseLogger } from '../../services/logging/logger';
import { ApplicationProductSelectionMode } from '../../models/organisation/application-product-selection-mode';
import { useNavigate } from 'react-router-dom';
import { closeDrawer } from '../../store/features/drawer/drawer-slice';

interface ContinousDrawerProps {
    departmentId: number,
    branchId: number,
}

export default function ContinousProductDrawer({ departmentId, branchId }: ContinousDrawerProps) {
    const continousProduct = useAppSelector(state => state.continousProduct);
    const basket = useAppSelector(state => state.basket);
    const dispatch = useAppDispatch();
    const [progressValue, setProgressValue] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [allowPreviousProduct, setAllowPreviousProduct] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const basketCheckRequestIdRef = useRef(0);
    const currentProductIdRef = useRef<number | null>(null);
    const logger = new DatabaseLogger();
    const navigate = useNavigate();

    currentProductIdRef.current = continousProduct.currentProduct?.id ?? null;

    useEffect(() => {
        if (departmentId) {
            dispatch(fetchSubDepartments(departmentId))
        }

    }, [departmentId])

    useEffect(() => {
        const activeCategories = continousProduct.subDepartments?.subDepartments.filter(x => x.active) ?? [];
        if (activeCategories.length === 1 && !continousProduct.selectedSubDepartment) {
            const subDepartment = activeCategories[0];
            if (subDepartment) {
                resetSelections(false);
                dispatch(setSelectedSubDepartment(subDepartment));
                dispatch(fetchProducts(subDepartment.id));
            }
        }
    }, [continousProduct.subDepartments, continousProduct.selectedSubDepartment]);


    useEffect(() => {
        resetSelections(true);
    }, [basket.basket?.id])


    const handleSubDepartmentClick = (subDepartment: ApplicationSubDepartment) => {

        if (subDepartment.productSelectionMode === ApplicationProductSelectionMode.AddOn) {
            resetSelections(false);
            navigate(`${subDepartment.id}/products`)
            dispatch(closeDrawer())
            return;
        }

        if (!continousProduct?.completedSubDepartments.find(x => x.id == subDepartment.id)) {
            resetSelections(false);
            dispatch(setSelectedSubDepartment(subDepartment))
            dispatch(fetchProducts(subDepartment.id));
        }
    }

    const nextProduct = async (expectedProductId: number | null = continousProduct.currentProduct?.id ?? null) => {
        if (
            expectedProductId == null ||
            currentProductIdRef.current !== expectedProductId ||
            !continousProduct.currentProduct ||
            !continousProduct.products?.products
        ) {
            return;
        }

        if (continousProduct.currentProduct && continousProduct.products?.products) {
            const { products, count } = continousProduct.products;
            const currentIndex = products.indexOf(continousProduct.currentProduct);

            if (currentIndex === -1) {
                logger.error(
                    "Current product not found in products array",
                    { basket: basket.basket },
                    { currentIndex: currentIndex },
                    { totalCount: count },
                    { productsLength: products.length },
                    { currentProduct: continousProduct.currentProduct });
                return;
            }

            if (currentIndex === count - 1) {
                dispatch(setCompletedSubDepartments(continousProduct.selectedSubDepartment));
                resetSelections(false);
                return;
            }

            const nextIndex = currentIndex + 1;

            if (
                nextIndex === products.length - 3 &&
                continousProduct?.selectedSubDepartment
            ) {
                await dispatch(fetchProducts(continousProduct.selectedSubDepartment.id));
            }

            if (nextIndex < products.length) {
                const nextProduct = products[nextIndex];

                if (nextProduct) {
                    dispatch(setCurrentProduct(nextProduct));
                }

                const currentProductIndex = nextIndex + 1;
                const totalProducts = count ?? 0;
                const progress = (currentProductIndex / totalProducts) * 100;

                setProgressValue(progress);
            }
        }
    };

    const previousProduct = async () => {
        if (continousProduct.currentProduct && continousProduct.products?.products) {
            const { products, count } = continousProduct.products;
            const currentIndex = products.indexOf(continousProduct.currentProduct);

            if (currentIndex === -1) {
                logger.error(
                    "Current product not found in products array",
                    { basket: basket.basket },
                    { currentIndex: currentIndex },
                    { totalCount: count },
                    { productsLength: products.length },
                    { currentProduct: continousProduct.currentProduct });
                return;
            }

            const previousIndex = currentIndex - 1;

            if (previousIndex < 0) {
                logger.log(
                    "Already at the first product",
                    { basket: basket.basket },
                    { currentIndex },
                    { previousIndex })
                return;
            }

            const previousProduct = products[previousIndex];

            if (previousProduct) {
                setAllowPreviousProduct(true);
                dispatch(setCurrentProduct(previousProduct));

                const currentProductIndex = previousIndex + 1;
                const totalProducts = count ?? 0;
                const progress = (currentProductIndex / totalProducts) * 100;

                setProgressValue(progress);
            }
        }
    };

    // skips products that are in basket already.
    useEffect(() => {
        if (basket.basket && continousProduct.currentProduct && !allowPreviousProduct) {
            const requestId = ++basketCheckRequestIdRef.current;
            const productId = continousProduct.currentProduct.id;
            setLoading(true);
            dispatch(fetchByProductId({ basketId: basket.basket.id, productId }))
                .then((response) => {
                    // Ignore stale responses from previous products.
                    if (requestId !== basketCheckRequestIdRef.current || currentProductIdRef.current !== productId) {
                        return;
                    }

                    var basketItem = response.payload as ApplicationBasketItem;
                    if (basketItem?.id > 0) {
                        nextProduct(productId);
                    }
                }).finally(() => {
                    if (requestId !== basketCheckRequestIdRef.current) {
                        return;
                    }

                    if (timeoutId) {
                        clearTimeout(timeoutId);
                    }
                    const id = setTimeout(() => {
                        setLoading(false)
                    }, 500);
                    setTimeoutId(id);
                })
        }

        return () => {
            basketCheckRequestIdRef.current += 1;
        };
    }, [continousProduct.currentProduct])

    useEffect(() => {
        if (continousProduct.products?.products.length == 0) {
            dispatch(showError("No products are setup for this category"))
        }
    }, [continousProduct.products])


    const resetSelections = (resetCompleted: boolean) => {
        dispatch(setCurrentProduct(null));
        dispatch(resetProducts());
        dispatch(setSelectedSubDepartment(null));

        if (resetCompleted) {
            dispatch(resetCompletedSubDepartments())
        }
    }

    return (
        <>
            {!continousProduct.selectedSubDepartment || !continousProduct.currentProduct ? (
                <>
                    <Title>Select Sub Department</Title>
                    <SubDepartmentSelection>
                        {continousProduct.subDepartments?.subDepartments.filter(x => x.active).map(x => {
                            const completed = continousProduct.completedSubDepartments.find(cc => cc.id == x.id) != null;
                            return (<SubDepartment
                                completed={completed}
                                key={`subDepartment_${x.id}`}
                                onClick={() => handleSubDepartmentClick(x)}>
                                {x.name}
                                <SubDepartmentDescription>
                                    {x.description}
                                </SubDepartmentDescription>
                                {completed && (
                                    <div>
                                        <GoogleIcon isContrast id={ICON_ID.CheckCircle}></GoogleIcon><span>Complete</span>
                                    </div>
                                )}
                            </SubDepartment>
                            )
                        })}
                    </SubDepartmentSelection>
                </>
            ) : (
                <>
                    <ItemIndicator>
                        {(continousProduct?.products?.products.indexOf(continousProduct.currentProduct) ?? 0) + 1}  / {continousProduct?.products?.count}
                    </ItemIndicator>
                    <LinearProgress variant="determinate" value={progressValue} sx={{ mb: 2 }} />
                    {continousProduct.products && continousProduct.products.products.indexOf(continousProduct.currentProduct) > 0 && (
                        <BackButton>
                            <Button id="btnBack" label='Back' variant="outlined" icon={ICON_ID.ArrowBack} onClick={() => previousProduct()}></Button>
                        </BackButton>
                    )}
                    {loading && <LoadingOverlay><LoadingSpinner ></LoadingSpinner></LoadingOverlay>}
                    <ProductDrawer branchId={branchId} productId={continousProduct.currentProduct.id} basketCheck={allowPreviousProduct} addToBasketCallback={() => nextProduct(continousProduct.currentProduct?.id ?? null)}></ProductDrawer >
                </>
            )
            }
        </>

    )

}

const LoadingOverlay = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    background: white;
    display: flex;
    justify-content: center;
    z-index:3;
`
const ItemIndicator = styled.div`
    text-align:center;
`


const BackButton = styled.div`
        max-width:10px;
        margin-left:10px;
    `

const Title = styled.div`
    font-size:1.3rem;
    font-weight:500;
    margin-bottom:15px;
    padding-bottom:10px;
    width:100%;
    text-align:center;
    border-bottom: 1px solid ${(props) => props.theme.application.scheme.primary};
`

const SubDepartmentSelection = styled.div`
    display:flex;
    justify-content: space-evenly;    
    font-weight:600;
    font-size:1.2rem;
    flex-wrap: wrap;
    margin-bottom:20px;
`
interface SubDepartmentProps {
    completed: boolean
}
const SubDepartment = styled.div<SubDepartmentProps>`         
    font-size:1.1rem;
    width: 100%;
    height:40px;
    border-bottom:1px solid ${(props) => props.theme.application.border};;
    padding:10px 15px;
    font-weight:600;
    background: ${(props) => (props.completed ? props.theme.application.scheme.success : "")};
    cursor:pointer;   

    color:  ${(props) => (props.completed ? props.theme.application.scheme.contrast : props.theme.application.scheme.neutral)};      
    opacity: ${(props) => (props.completed ? 0.6 : 1)};

    &:hover{
            opacity:0.9;       
            background:  ${(props) => props.theme.application.hover};
        }
         
    div{
        font-size:0.9rem;
        font-weight:400;
        span{
        margin-right:5px;
        margin-left:0px;
        }
    }
`
const SubDepartmentDescription = styled.div`
    margin-top:3px;
    font-size:1rem;
    padding-right:10px;
    font-weight:400;
`
